/*
  GraphPage.jsx
*/

// Npm.
import React, { useState, useEffect, useRef } from 'react';

// Utils.
import deleteNodeFromGraph from '../../utils/deleteNodeFromGraph';

// Components.
import ForceGraph from '../ForceGraph';
import IconButton from '../IconButton';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import RightClickMenu from './RightClickMenu';
import OutputDialog from './OutputDialog';

// Icons.
import SettingsIcon from '../../icons/Settings';
import MenuIcon from '../../icons/MenuOpen';
import DialogIcon from '../../icons/PlayCircle';
import UndoIcon from '../../icons/Undo';
import RedoIcon from '../../icons/Redo';

// Hooks.
import useIsSize from '../../hooks/useIsSize';
import useReadOnly from '../../hooks/useReadOnly';

// makes.
import makeStyles from '../../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    height: '100%',
  },
  settingsButton: {
    position: 'absolute',
    top: theme.space(2),
    left: theme.space(2),
    zIndex: 1,
  },
  menuButton: {
    position: 'absolute',
    top: theme.space(2),
    right: theme.space(2),
    zIndex: 1,
  },
  dialogButton: {
    position: 'absolute',
    top: theme.space(2),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1,
  },
  undoButton: {
    position: 'absolute',
    bottom: theme.space(2),
    left: '50%',
    transform: 'translateX(-100%)',
    zIndex: 1,
  },
  redoButton: {
    position: 'absolute',
    bottom: theme.space(2),
    left: '50%',
    zIndex: 1,
  },
  graphContainer: {
    height: '100%',
    display: 'flex',
    '& > .mid': {
      height: '100%',
      flex: '1 1 auto',
      minWidth: 0,
    }
  },
  noneText: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    fontStyle: 'italic',
    fontWeight: 300,
    maxHeight: '100%',
    padding: theme.space(1),
    transform: 'translateY(-50%)',
    textAlign: 'center',
    overflow: 'hidden',
    color: theme.color_text_disabled,
    pointerEvents: 'none',
  }
}));

/**
 * Helper to toggle collapsed or expanded state on node.
 */
function toggleCollapsedOnNode(node) {
  const next = {
    ...node,
    _botech: { ...node._botech }
  };
  if (next._botech.collapsed) {
    delete next._botech.collapsed;
  } else {
    next._botech.collapsed = true;
  }
  return next;
}

/**
 * Renders the force graph across the screen.
 */
export default function GraphPage({
  disableThemeToggle,
  graphData,
  graphDataHistory,
  graphSettings,
  onChangeGraphData,
  onChangeGraphSettings,
}) {

  const canvasRef = useRef();
  const forceGraphRef = useRef();
  const classes = useStyles();
  const readOnly = useReadOnly();
  const isSmall = useIsSize('sm');

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [rightClickProps, setRightClickProps] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);

  // If the user moves from large to small screen, close the sidebars.

  useEffect(() => {
    if (isSmall) {
      setSettingsOpen(false);
      setMenuOpen(false);
    }
  }, [isSmall]);

  // Handlers.

  function handleClickSettings() {
    setSettingsOpen(true);
    setRightClickProps(null);
  }

  function handleClickMenu() {
    setMenuOpen(true);
    setRightClickProps(null);
  }

  function handleClickDialog() {
    setDialogOpen(true);
    setRightClickProps(null);
  }

  function handleCloseSettings() {
    setSettingsOpen(false);
    setRightClickProps(null);
  }

  function handleCloseMenu() {
    setMenuOpen(false);
    setRightClickProps(null);
  }

  function handleCloseDialog() {
    setDialogOpen(false);
  }

  function handleLinkClick(link) {
    setSelectedNode(null);
    setSelectedLink(link);
    setMenuOpen(true);
    setRightClickProps(null);
  }

  function handleNodeClick(node) {
    setSelectedLink(null);
    setSelectedNode(node);
    setMenuOpen(true);
    setRightClickProps(null);
  }

  function handleChangeSelectedNode(node) {
    setSelectedNode(node);
    setMenuOpen(true);
  }

  function handleChangeSelectedLink(link) {
    setSelectedLink(link);
    setMenuOpen(true);
  }

  function handleBackgroundClick(event) {
    setSelectedLink(null);
    setSelectedNode(null);
    setRightClickProps(null);
  }

  function handleBackgroundRightClick(event, usefulProps) {
    event.preventDefault();
    setRightClickProps({ xPos: event.clientX, yPos: event.clientY, ...usefulProps });
  }

  function handleNodeRightClick(node, event) {
    event.preventDefault();
    setRightClickProps({ node, xPos: event.clientX, yPos: event.clientY });
  }

  function handleLinkRightClick(link, event) {
    event.preventDefault();
    setRightClickProps({ link, xPos: event.clientX, yPos: event.clientY });
  }

  function handleCloseRightClickMenu() {
    setRightClickProps(null);
  }

  // Undo and redo handlers.

  const dataIndex = graphDataHistory.indexOf(graphData);
  const canUndo = dataIndex > 0;
  const canRedo = dataIndex >= 0 && dataIndex < graphDataHistory.length - 1;

  const undoHistory = canUndo ? graphDataHistory[dataIndex - 1] : null;
  const redoHistory = canRedo ? graphDataHistory[dataIndex + 1] : null;

  function handleClickUndo() {
    if (onChangeGraphData) {
      onChangeGraphData(undoHistory);
    }
  }

  function handleClickRedo() {
    if (onChangeGraphData) {
      onChangeGraphData(redoHistory);
    }
  }

  // Effect for triggering undo/redo using the keyboard.
  // NOTE: Currently just detects on the body, meaning the user needs to click or re-click
  // the canvas before pressing one of the keys.

  useEffect(() => {
    if (undoHistory && onChangeGraphData) {

      function handleKeyDown(event) {
        if (!event.defaultPrevented && event.target === document.body) {
          if (event.key === 'z' && !event.shiftKey && (event.metaKey || event.ctrlKey)) {
            onChangeGraphData(undoHistory);
          }
        }
      }

      document.addEventListener('keydown', handleKeyDown, false);
      return () => {
        document.removeEventListener('keydown', handleKeyDown, false);
      };

    }
  }, [undoHistory, onChangeGraphData]);

  useEffect(() => {
    if (redoHistory && onChangeGraphData) {

      function handleKeyDown(event) {
        if (!event.defaultPrevented && event.target === document.body) {
          if (event.key === 'z' && event.shiftKey && (event.metaKey || event.ctrlKey)) {
            onChangeGraphData(redoHistory);
          }
        }
      }

      document.addEventListener('keydown', handleKeyDown, false);
      return () => {
        document.removeEventListener('keydown', handleKeyDown, false);
      };

    }
  }, [redoHistory, onChangeGraphData]);

  // Effect to check if the selected node or link still exists by ID, in case the user is undoing/redoing.
  // Either update or remove it.

  useEffect(() => {
    if (selectedNode?.id) {
      const node = graphData.nodes.find(node => node.id === selectedNode.id) || null;
      if (node !== selectedNode) {
        setSelectedNode(node);
      }
    }
  }, [graphData, selectedNode]);

  useEffect(() => {
    if (selectedLink && !graphData.links.includes(selectedLink)) {
      setSelectedLink(null);  // TODO: Needs access to previous index or something to work this out.
    }
  }, [graphData, selectedLink]);

  // Effect to check for keyboard shortcuts on the document to deselect or
  // delete selected items.
  // NOTE: Currently just detects on the body, meaning the user needs to click or re-click
  // the canvas before pressing one of the keys.

  useEffect(() => {
    if (selectedNode || selectedLink) {

      function handleKeyDown(event) {
        if (!event.defaultPrevented && event.target === document.body) {
          if (event.key === 'Escape') {

            // Escape key will deselect.
            if (selectedLink) {
              setSelectedLink(null);
            }
            if (selectedNode) {
              setSelectedNode(null);
            }

          } else if (event.key === 'Backspace') {

            // Backspace will delete the selected node/link.
            if (!readOnly && onChangeGraphData) {
              onChangeGraphData(graphData => {
                let next = graphData;
                if (selectedNode) {
                  next = deleteNodeFromGraph(next, selectedNode);
                }
                if (selectedLink) {
                  next = {
                    ...next,
                    links: next.links.filter(obj => obj !== selectedLink),
                  };
                }
                return next;
              });
            }

          } else if (event.key === ' ') {

            // Spacebar will expand/collapse the selected node.
            if (selectedNode) {
              onChangeGraphData(graphData => {
                const next = toggleCollapsedOnNode(selectedNode);
                return {
                  ...graphData,
                  nodes: graphData.nodes.map(obj => obj === selectedNode ? next : obj),
                  links: graphData.links.map(obj => obj.source === selectedNode || obj.target === selectedNode ? {
                    ...obj,
                    source: obj.source === selectedNode ? next : obj.source,
                    target: obj.target === selectedNode ? next : obj.target,
                  } : obj),
                };
              });
            }

          }
        }
      }

      document.addEventListener('keydown', handleKeyDown, false);
      return () => {
        document.removeEventListener('keydown', handleKeyDown, false);
      };

    }
  }, [selectedNode, selectedLink, setSelectedNode, setSelectedLink, onChangeGraphData, readOnly]);

  // Props.

  const disableButtons = dialogOpen || (isSmall && (menuOpen || settingsOpen));

  return (
    <div className={classes.root}>

      {
        rightClickProps != null &&
        <RightClickMenu
          graphData={graphData}
          selectedNode={selectedNode}
          onClose={handleCloseRightClickMenu}
          onChangeGraphData={onChangeGraphData}
          onChangeSelectedNode={handleChangeSelectedNode}
          {...rightClickProps}
        />
      }

      <div className={`MainGraphContainer ${classes.graphContainer}`}>

        <IconButton
          className={classes.settingsButton}
          title="Settings"
          aria-label="settings"
          onClick={handleClickSettings}
          disabled={settingsOpen || disableButtons}
        >
          <SettingsIcon />
        </IconButton>

        <LeftSidebar
          disableThemeToggle={disableThemeToggle}
          open={settingsOpen}
          onClose={handleCloseSettings}
          disabled={dialogOpen}
          isSmall={isSmall}
          graphData={graphData}
          graphSettings={graphSettings}
          tagFilter={tagFilter}
          canvasRef={canvasRef}
          forceGraphRef={forceGraphRef}
          onChangeGraphSettings={onChangeGraphSettings}
          onChangeTagFilter={setTagFilter}
        />

        <ForceGraph
          className="mid"
          graphData={graphData}
          graphSettings={graphSettings}
          tagFilter={tagFilter}
          selectedNode={selectedNode}
          selectedLink={selectedLink}
          onNodeClick={handleNodeClick}
          onLinkClick={handleLinkClick}
          onLinkRightClick={handleLinkRightClick}
          onBackgroundClick={handleBackgroundClick}
          onBackgroundRightClick={handleBackgroundRightClick}
          onNodeRightClick={handleNodeRightClick}
          canvasRef={canvasRef}
          forceGraphRef={forceGraphRef}
        />

        {
          !readOnly &&
          <IconButton
            className={classes.dialogButton}
            title="Output"
            aria-label="output"
            onClick={handleClickDialog}
            disabled={dialogOpen || disableButtons}
          >
            <DialogIcon />
          </IconButton>
        }

        {
          !readOnly &&
          <OutputDialog
            graphData={graphData}
            open={dialogOpen}
            onClose={handleCloseDialog}
            onChangeGraphData={onChangeGraphData}
          />
        }

        <IconButton
          className={classes.menuButton}
          title="Menu"
          aria-label="menu"
          onClick={handleClickMenu}
          disabled={menuOpen || disableButtons}
        >
          <MenuIcon />
        </IconButton>

        <RightSidebar
          open={menuOpen}
          onClose={handleCloseMenu}
          disabled={dialogOpen}
          isSmall={isSmall}
          graphData={graphData}
          selectedNode={selectedNode}
          selectedLink={selectedLink}
          onChangeGraphData={onChangeGraphData}
          onChangeSelectedNode={handleChangeSelectedNode}
          onChangeSelectedLink={handleChangeSelectedLink}
        />

        <IconButton
          className={classes.undoButton}
          title="Undo"
          aria-label="undo"
          onClick={handleClickUndo}
          disabled={dialogOpen || disableButtons || !canUndo}
        >
          <UndoIcon />
        </IconButton>

        <IconButton
          className={classes.redoButton}
          title="Redo"
          aria-label="redo"
          onClick={handleClickRedo}
          disabled={dialogOpen || disableButtons || !canRedo}
        >
          <RedoIcon />
        </IconButton>

        {
          graphData.nodes.length === 0 &&
          <div className={classes.noneText}>
            { readOnly ? 'The Graph is empty.' : 'Right-Click or use the Menu Button to create a Node.' }
          </div>
        }

      </div>
    </div>
  );
}
